// src/components/Home/ProjectCard.js

import * as React from "react";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { styled } from "@mui/material/styles";

// Styled component for Card - COMPACT WIDTH CONSTRAINTS
const StyledCard = styled(Card)(({ theme }) => ({
  // Compact height for better fit
  height: 360,
  width: '100%',  // Take full width of grid container
  maxWidth: '400px', // Max width to prevent cards from being too wide
  margin: '0 auto', // Center if container is wider
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: '#FFFFFF',
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0px 8px 16px rgba(0,0,0,0.12)",
  },
}));

// Image container with fixed aspect ratio
const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  paddingTop: '60%', // 5:3 aspect ratio (more rectangular, less height)
  overflow: 'hidden',
  backgroundColor: '#f5f5f5',
}));

// Styled CardMedia positioned absolutely within container
const StyledCardMedia = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

// Styled CardContent with proper constraints
const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  minHeight: 0, // Allow shrinking
}));

// Title with ellipsis for overflow
const TitleTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: "#2C2416",
  fontSize: '1.05rem',
  lineHeight: 1.3,
  marginBottom: theme.spacing(1),
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minHeight: '2.6em', // Reserve space for 2 lines
}));

// Description with line clamping
const DescriptionTypography = styled(Typography)(({ theme }) => ({
  color: "#5C4033",
  fontSize: '0.875rem',
  lineHeight: 1.5,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flexGrow: 1,
}));

export default function ProjectCard({ project, onClick }) {
  // Handle missing image gracefully
  const [imageError, setImageError] = React.useState(false);
  
  // Debug log to check if onClick is received
  console.log("ProjectCard - onClick prop received:", !!onClick, "Project:", project?.title);
  
  // Fallback placeholder image
  const placeholderImage = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='240' viewBox='0 0 400 240'%3E%3Crect width='400' height='240' fill='%23F0EBE5'/%3E%3Cg fill='%23C4A57B' opacity='0.3'%3E%3Crect x='140' y='100' width='120' height='80' rx='4'/%3E%3Cpolygon points='170,80 200,50 230,80 260,40 290,80 290,100 170,100'/%3E%3Ccircle cx='190' cy='60' r='12'/%3E%3C/g%3E%3C/svg%3E`;
  
  // Use the project image or fallback to placeholder
  const imageSource = imageError || !project?.mainImage 
    ? placeholderImage 
    : project.mainImage;

  const handleImageError = (e) => {
    console.warn("Failed to load image:", project?.mainImage);
    setImageError(true);
  };

  const handleClick = (e) => {
    // Prevent event bubbling
    e.stopPropagation();
    console.log("ProjectCard clicked, calling onClick with project:", project);
    if (onClick && project) {
      onClick(project);
    }
  };

  return (
    <StyledCard>
      <CardActionArea 
        onClick={handleClick}
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          overflow: 'hidden',
        }}
      >
        <ImageContainer>
          <StyledCardMedia
            src={imageSource}
            alt={project?.title || "Project"}
            onError={handleImageError}
            loading="lazy"
          />
        </ImageContainer>
        
        <StyledCardContent>
          <TitleTypography variant="h6" component="h3">
            {project?.title || "Untitled Project"}
          </TitleTypography>
          
          <DescriptionTypography variant="body2">
            {project?.shortDescription || "No description available"}
          </DescriptionTypography>
        </StyledCardContent>
      </CardActionArea>
      
      <CardActions 
        sx={{ 
          padding: theme => theme.spacing(0, 2, 1.5),
          paddingTop: 0,
        }}
      >
        <Button
          size="small"
          onClick={handleClick}
          sx={{ 
            color: "#D2691E",
            textTransform: "none",
            fontWeight: 600,
            fontSize: '0.875rem',
            padding: '4px 8px',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'rgba(210, 105, 30, 0.08)',
              textDecoration: 'none',
            },
            '& .MuiButton-endIcon': {
              marginLeft: '4px',
              '& svg': {
                fontSize: '1rem',
              }
            }
          }}
          endIcon={<ArrowForwardIcon />}
        >
          View Details
        </Button>
      </CardActions>
    </StyledCard>
  );
}